import type { QueryKey } from '@tanstack/react-query';

/**
 * The one QueryClient capability this helper needs — structural so the test
 * can hand in a bare spy without re-implementing the full client surface.
 */
export interface IWorkflowQueryInvalidator {
  invalidateQueries(filters: { queryKey: QueryKey }): Promise<unknown>;
}

/**
 * Invalidate a workflow's row and its versions list IN THE SAME TICK.
 *
 * This is the root-cause fix for the false "data inconsistency" hydration
 * banner: awaiting the two invalidations sequentially opened a window where
 * the fresh workflow row (new currentVersionId) joined a stale versions
 * list, which the classifier misread as corruption. The classifier also
 * guards on versions.isFetching, but that is defense in depth — batching
 * here removes the ordering assumption at the source, for every save path
 * that goes through this helper (graph save + schema editor).
 *
 * Extracted so a test can pin the batching: a refactor back to sequential
 * awaits would reintroduce the window silently, with the isFetching belt
 * hiding the symptom well enough that nobody notices.
 */
export function invalidateWorkflowQueries(
  qc: IWorkflowQueryInvalidator,
  workflowId: string,
  extraKeys: QueryKey[] = [],
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['workflow', workflowId] }),
    qc.invalidateQueries({ queryKey: ['workflow-versions', workflowId] }),
    ...extraKeys.map((queryKey) => qc.invalidateQueries({ queryKey })),
  ]);
}
